import { DateTime } from 'luxon';

import {
	ACCESS_TOKEN_KEY,
	ACCESS_TOKEN_EXPIRE_AT_KEY,
	USER_ROLE,
	APP_USER_ID,
} from '@/constants';
import {
	AUTH_KEY,
	AUTH_EXPIRATION_DATE_KEY,
	AUTH_SUBJECT_KEY,
	REFRESH_TOKEN_KEY,
	REFRESH_TOKEN_EXPIRATION_KEY,
	ACCESS_TOKEN_EXPIRATION_KEY,
	FEATURES_KEY,
	IS_SUBSCRIBED_KEY,
	parseJwt,
} from '@shared/auth/authentication.hooks';
import { notifyAuthStateChange } from './auth-state';
import { clearSessionCache } from './session-teardown';

/**
 * Read/write helpers for the Cognito JWT access/refresh token pair — the
 * only file the Cognito flow (`services/auth/cognito.ts`,
 * `cognitoCallback.ts`, and the `basic`/`magshop` templates that configure
 * them) touches localStorage through for these keys.
 */

export function getAccessToken(): string | null {
	return localStorage.getItem(ACCESS_TOKEN_KEY);
}

export function getRefreshToken(): string | null {
	return localStorage.getItem(REFRESH_TOKEN_KEY);
}

export function isRefreshTokenUsable(): boolean {
	const refreshToken = getRefreshToken();
	const refreshTokenExp = localStorage.getItem(REFRESH_TOKEN_EXPIRATION_KEY);
	if (!refreshToken || !refreshTokenExp) {
		return false;
	}

	return DateTime.now() < DateTime.fromISO(refreshTokenExp);
}

export function setAccessToken(accessToken: string, expirationDate: string): void {
	localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
	localStorage.setItem(ACCESS_TOKEN_EXPIRATION_KEY, expirationDate);
}

/**
 * Every localStorage key a Cognito session can touch, byte-identical to the
 * union `basic`'s and `magshop`'s previously-separate `clearTokens()`
 * implementations already cleared (`magshop` imported `basic`'s verbatim).
 * Also the full cross-auth-type allowlist a general-purpose "wipe
 * everything" action (`widgets/about`'s Clear Cache) must spare -- it
 * additionally carries `AUTH_EXPIRATION_DATE_KEY`/`AUTH_SUBJECT_KEY`, which
 * the direct `login`/`jwt` flow (`authentication.hooks.tsx`) writes but
 * Cognito never does, so a Cognito-only list would have let that button log
 * a `login`/`jwt` reader out.
 */
export const AUTH_STORAGE_KEYS = [
	AUTH_KEY,
	AUTH_EXPIRATION_DATE_KEY,
	AUTH_SUBJECT_KEY,
	USER_ROLE,
	ACCESS_TOKEN_KEY,
	ACCESS_TOKEN_EXPIRATION_KEY,
	REFRESH_TOKEN_KEY,
	ACCESS_TOKEN_EXPIRE_AT_KEY,
	REFRESH_TOKEN_EXPIRATION_KEY,
	APP_USER_ID,
	FEATURES_KEY,
	IS_SUBSCRIBED_KEY,
];

export function clearTokens(redirect?: string): void {
	for (const key of AUTH_STORAGE_KEYS) {
		if (localStorage.getItem(key)) {
			localStorage.removeItem(key);
		}
	}
	notifyAuthStateChange();

	// Clearing the token keys alone leaves the previous session's fetched
	// content in the query cache and in the persister's IndexedDB entries.
	// On the redirect paths below the reload used to hide that; on the
	// `clearTokens()` calls that pass no redirect (cognito.ts's invalid-session
	// and refresh-failure branches) nothing cleared it at all.
	const cleared = clearSessionCache();

	if (redirect) {
		// The redirect waits for the purge rather than racing it: `location.href`
		// starts an async navigation, so an in-flight IndexedDB write is not
		// guaranteed to land before the document is torn down. `clearSessionCache`
		// never rejects and is time-bounded, so this always fires. Same
		// purge-then-navigate ordering as `features/login/ui/logout.tsx`.
		void cleared.finally(() => {
			// Deferring the redirect means this callback runs a turn later,
			// by which point the document may be gone -- a page already
			// navigating away, or a test whose environment has been torn
			// down. Reading `location` then is a ReferenceError, not a
			// no-op, and it surfaces as an unhandled rejection rather than
			// anywhere useful. Guarded rather than left to chance: if
			// there's no document left, there is nothing to navigate.
			if (typeof location === 'undefined') {
				return;
			}
			location.href = redirect;
		});
	}
}

/**
 * The exact key list `features/oauth-callback/Callback.hooks.ts`'s
 * original `clearTokenData()` used inside `saveTokens` — narrower than
 * `clearTokens()` above (no `ACCESS_TOKEN_EXPIRE_AT_KEY`, no `APP_USER_ID`).
 * `ACCESS_TOKEN_EXPIRE_AT_KEY` in particular belongs to an unrelated
 * feature (the drafts/preview-link expiry in `pages/drafts/`), so clearing
 * it here would wipe state a Cognito token exchange has no business
 * touching. Kept as a separate list rather than reusing `clearTokens()`.
 */
const SAVE_TOKENS_CLEAR_KEYS = [
	AUTH_KEY,
	USER_ROLE,
	ACCESS_TOKEN_KEY,
	ACCESS_TOKEN_EXPIRATION_KEY,
	REFRESH_TOKEN_KEY,
	REFRESH_TOKEN_EXPIRATION_KEY,
	IS_SUBSCRIBED_KEY,
	FEATURES_KEY,
];

/**
 * Persists the token-exchange response from the `/callback` code exchange.
 * Ported verbatim from `features/oauth-callback/callback.hooks.ts`'s
 * `saveTokens`/`clearTokenData`, which `magshop`'s callback already reused
 * as-is.
 */
export function saveTokens(content: CognitoTokenResponse): void {
	const { access_token, refresh_token, expires } = content;

	const accessTokenData = parseJwt(access_token);
	const refreshTokenData = parseJwt(refresh_token);

	const accessExpirationDate = DateTime.now()
		.plus({ seconds: expires })
		.toISO();
	const refreshExpirationDate = DateTime.fromSeconds(
		refreshTokenData.exp,
	).toISO();

	for (const key of SAVE_TOKENS_CLEAR_KEYS) {
		if (localStorage.getItem(key)) {
			localStorage.removeItem(key);
		}
	}

	localStorage.setItem(AUTH_KEY, 'true');
	localStorage.setItem(USER_ROLE, 'user');
	if (accessTokenData.sub) {
		localStorage.setItem(APP_USER_ID, accessTokenData.sub);
	} else if (localStorage.getItem(APP_USER_ID)) {
		localStorage.removeItem(APP_USER_ID);
	}

	setAccessToken(access_token, `${accessExpirationDate}`);

	localStorage.setItem(REFRESH_TOKEN_KEY, refresh_token);
	if (refreshExpirationDate) {
		localStorage.setItem(REFRESH_TOKEN_EXPIRATION_KEY, refreshExpirationDate);
	}

	if (accessTokenData.entitlements) {
		if (accessTokenData.entitlements?.subscription) {
			localStorage.setItem(IS_SUBSCRIBED_KEY, 'true');
		}
		if (accessTokenData.entitlements.features) {
			localStorage.setItem(
				FEATURES_KEY,
				JSON.stringify(accessTokenData.entitlements.features),
			);
		}
	}

	notifyAuthStateChange();

	// A genuinely new login, not a token refresh -- see clearTokens()'s own
	// call to this and clearSessionCache's doc comment for why a session
	// boundary in either direction wipes offline content: whatever the
	// *previous* session on this device downloaded/favorited must not
	// silently carry over into this one. Fire-and-forget; clearSessionCache
	// never rejects and the login flow doesn't wait on it.
	void clearSessionCache();
}

export interface CognitoTokenResponse {
	access_token: string;
	refresh_token: string;
	expires: number;
}

export class UserNotFoundError extends Error {
	constructor(message: string) {
		super(message);
		this.name = 'UserNotFound';
	}
}
